<script setup lang="ts">
  import {
    computed,
    getCurrentInstance,
    onBeforeUnmount,
    ref,
    watch,
  } from 'vue';
  import PosUiIcon from '@/components/pos-v2/ui/PosUiIcon.vue';
  import {
    clearAssistantPill,
    setAssistantPill,
  } from '@/composition/assistant/assistant-pill';
  import { useAssistantStore } from '@/composition/assistant/use-assistant-store';
  import {
    assistantAriaShortcut,
    assistantShortcutKeys,
  } from '@/domain/assistant/assistant-shortcut';

  /**
   * The top-bar «Asistente» pill (plan §9.4): toggles the panel, shows the
   * shortcut, and a dot while a turn runs (working) or a change waits for the
   * user's OK (pending). Icon only on phones.
   */
  const store = useAssistantStore();
  const vm = getCurrentInstance()!.proxy;

  const platform = typeof navigator === 'undefined' ? '' : navigator.platform;
  const keys = assistantShortcutKeys(platform);
  const ariaShortcut = assistantAriaShortcut(platform);

  const button = ref<HTMLButtonElement | null>(null);

  const ariaLabel = computed(() => {
    const label = String(vm.$t('assistant.pill.label'));
    if (store.dot === 'working')
      return `${label}. ${vm.$t('assistant.pill.working')}`;
    if (store.dot === 'pending')
      return `${label}. ${vm.$t('assistant.pill.pending')}`;
    return label;
  });

  // The coachmark (rendered at the app root) anchors to this element.
  watch(
    button,
    (element, previous) => {
      if (previous) clearAssistantPill(previous);
      if (element) setAssistantPill(element);
    },
    { immediate: true }
  );

  onBeforeUnmount(() => clearAssistantPill(button.value));

  function toggle() {
    store.toggle('pill');
  }
</script>

<template>
  <button
    v-show="store.available"
    ref="button"
    type="button"
    class="v2-assistant-top-bar-button"
    :class="{ 'is-open': store.shown }"
    :aria-pressed="store.shown ? 'true' : 'false'"
    :aria-label="ariaLabel"
    :aria-keyshortcuts="ariaShortcut"
    :title="$t('assistant.pill.title', { keys })"
    @click="toggle"
  >
    <PosUiIcon name="creation" class="pill-icon" />
    <span class="pill-label">{{ $t('assistant.pill.label') }}</span>
    <kbd class="pill-kbd">{{ keys }}</kbd>
    <span
      v-if="store.dot"
      class="pill-dot"
      :class="`is-${store.dot}`"
      aria-hidden="true"
    />
  </button>
</template>

<style scoped lang="scss">
  .v2-assistant-top-bar-button {
    @apply tw-relative tw-mr-1.5 tw-flex tw-h-10 tw-shrink-0 tw-cursor-pointer tw-items-center tw-rounded-full tw-border tw-border-solid tw-border-neutral-200 tw-bg-surface tw-pl-2.5 tw-pr-3.5 tw-text-sm tw-font-medium tw-text-neutral-700 tw-transition-colors tw-duration-150;

    &:hover {
      @apply tw-bg-neutral-50;
    }

    &:focus-visible {
      @apply tw-outline-none tw-ring-2 tw-ring-orange-500/40;
    }

    // Open: the active-state role (the panel is «where you are»).
    &.is-open {
      @apply tw-border-orange-100 tw-bg-orange-50 tw-text-orange-700;

      .pill-kbd {
        @apply tw-hidden;
      }
    }

    .pill-icon {
      @apply tw-mr-1.5 tw-text-xl;
    }

    // Overrides Vuetify's dark `.v-application kbd`.
    .pill-kbd {
      @apply tw-ml-2 tw-rounded tw-border tw-border-solid tw-border-neutral-200 tw-bg-neutral-50 tw-px-1.5 tw-py-px tw-text-[11px] tw-font-normal tw-leading-4 tw-text-neutral-500;
      box-shadow: none;
      font-family: inherit;

      // Touch screens have no keyboard to press it on.
      @media (hover: none) {
        display: none;
      }
    }

    .pill-dot {
      @apply tw-absolute tw-h-2.5 tw-w-2.5 tw-rounded-full tw-border-2 tw-border-solid tw-border-surface;
      top: 5px;
      left: 24px;

      &.is-pending {
        @apply tw-bg-amber-500;
      }

      &.is-working {
        @apply tw-border-neutral-900 tw-bg-surface;
        animation: pill-dot-pulse 1.2s ease-in-out infinite;
      }
    }

    // Phones: a 44 px round icon button; the label goes (CSS, not v-if).
    @media (max-width: 599px) {
      @apply tw-mr-0.5 tw-h-11 tw-w-11 tw-justify-center tw-p-0;

      .pill-icon {
        @apply tw-mr-0;
      }

      .pill-label,
      .pill-kbd {
        display: none;
      }

      .pill-dot {
        top: 6px;
        left: 26px;
      }
    }
  }

  @keyframes pill-dot-pulse {
    50% {
      opacity: 0.35;
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .v2-assistant-top-bar-button .pill-dot.is-working {
      animation: none;
    }
  }
</style>
